import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Accordion, Button, Col, Container, Dropdown, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Navbar from '../../../Navbar';
import PageTitle from '../../../components/PageTitle';
import ShareGuideModal from '../ShareGuideModal';
import PublishGuideModal from '../PublishGuideModal';
import {
  authHeaders,
  flashcardsFetch,
  formatGuideDate
} from './flashcardsUtils';
import GuideItemIcon from '../GuideItemIcon';
import '../guides.css';

export default function FlashcardDeckManagePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deck, setDeck] = useState(null);
  const [myDecks, setMyDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSections, setActiveSections] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [sectionModal, setSectionModal] = useState(false);
  const [sectionTitle, setSectionTitle] = useState('');
  const [setModal, setSetModal] = useState(null);
  const [setTitle, setSetTitle] = useState('');
  const [moveModal, setMoveModal] = useState(null);
  const [moveSectionId, setMoveSectionId] = useState('');
  const [busy, setBusy] = useState(false);
  const setDragRef = useRef(null);

  const loadMine = useCallback(async () => {
    try {
      const data = await flashcardsFetch('/mine', { headers: authHeaders() });
      setMyDecks((Array.isArray(data) ? data : []).filter((item) => item.type === 'deck'));
    } catch {
      setMyDecks([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await flashcardsFetch(`/decks/${id}`, { headers: authHeaders() });
      setDeck(data);
      setActiveSections((data.sections || []).map((s) => String(s.id)));
    } catch (err) {
      toast.error(err.message || 'Failed to load deck');
      navigate('/guides/flashcards');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
    loadMine();
  }, [load, loadMine]);

  const togglePublish = async () => {
    if (!deck.published) {
      setPublishOpen(true);
      return;
    }
    if (!window.confirm('Unpublish this deck?')) return;
    try {
      const res = await flashcardsFetch(`/decks/${id}/publish`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ published: false })
      });
      setDeck((d) => ({
        ...d,
        published: res.published,
        publishedAt: res.publishedAt,
        allowEdits: res.allowEdits,
        categoryId: res.categoryId,
        topicId: res.topicId,
        description: res.description ?? d.description
      }));
      toast.success('Unpublished');
      loadMine();
    } catch (err) {
      toast.error(err.message || 'Failed to update publish status');
    }
  };

  const addSection = async (e) => {
    e.preventDefault();
    if (!sectionTitle.trim()) return;
    setBusy(true);
    try {
      await flashcardsFetch(`/decks/${id}/sections`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ title: sectionTitle.trim() })
      });
      setSectionModal(false);
      setSectionTitle('');
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to add section');
    } finally {
      setBusy(false);
    }
  };

  const deleteSection = async (sectionId) => {
    if (!window.confirm('Delete this section and all flashcard sets inside it?')) return;
    try {
      await flashcardsFetch(`/sections/${sectionId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to delete section');
    }
  };

  const addSet = async (e) => {
    e.preventDefault();
    if (!setTitle.trim() || !setModal) return;
    setBusy(true);
    try {
      const created = await flashcardsFetch(`/sections/${setModal}/sets`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ title: setTitle.trim() })
      });
      setSetModal(null);
      setSetTitle('');
      navigate(`/guides/flashcards/sets/${created.id}/edit`);
    } catch (err) {
      toast.error(err.message || 'Failed to add flashcard set');
      setBusy(false);
    }
  };

  const deleteSet = async (setId) => {
    if (!window.confirm('Delete this flashcard set and all its cards?')) return;
    try {
      await flashcardsFetch(`/sets/${setId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to delete set');
    }
  };

  const detachSet = async (setId) => {
    if (!window.confirm('Remove this set from the deck? Cards are preserved as a standalone set.')) return;
    try {
      await flashcardsFetch(`/sets/${setId}/move`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({ sectionId: null })
      });
      toast.success('Set moved to standalone');
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to move set');
    }
  };

  const moveSet = async (e) => {
    e.preventDefault();
    if (!moveModal) return;
    setBusy(true);
    try {
      await flashcardsFetch(`/sets/${moveModal.id}/move`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({
          sectionId: moveSectionId === '' ? null : Number(moveSectionId)
        })
      });
      toast.success('Set moved');
      setMoveModal(null);
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to move set');
    } finally {
      setBusy(false);
    }
  };

  const reorderSectionSets = async (sectionId, orderedIds) => {
    setDeck((d) => {
      if (!d) return d;
      return {
        ...d,
        sections: (d.sections || []).map((s) => {
          if (Number(s.id) !== Number(sectionId)) return s;
          const byId = Object.fromEntries((s.sets || []).map((t) => [t.id, t]));
          return {
            ...s,
            sets: orderedIds
              .map((tid, i) => (byId[tid] ? { ...byId[tid], sortOrder: i } : null))
              .filter(Boolean)
          };
        })
      };
    });
    try {
      await flashcardsFetch(`/sections/${sectionId}/sets/reorder`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({ order: orderedIds })
      });
    } catch (err) {
      toast.error(err.message || 'Failed to reorder');
      await load();
    }
  };

  if (loading || !deck) {
    return (
      <div className="guides-app guides-loading-screen">
        <Navbar pageTitle="Deck" />
        <div className="guides-loading"><Spinner /></div>
      </div>
    );
  }

  const sections = [...(deck.sections || [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const setCount = sections.reduce((s, sec) => s + ((sec.sets || []).length), 0);
  const allSectionsFlat = sections;
  const canEdit = !!deck.canEdit;
  const canStudy = !!(deck.canStudy ?? deck.canEdit);
  const openSet = (setId) => {
    if (canStudy) navigate(`/guides/flashcards/sets/${setId}`);
    else navigate(`/guides/flashcards/sets/${setId}/study?view=browse`);
  };
  return (
    <div className="guides-app guides-topic-editor-page bg-inverse">
      <Navbar pageTitle={deck.title} />
      <PageTitle title={deck.title} />

      <Container fluid="lg" className="guides-topic-editor-workspace">
        <Row className="g-0 h-100">
          {canEdit && (
            <Col lg={3} md={3} className="border-end guides-topic-editor-rail">
              <div className="p-3 ps-1 pe-2">
                <div className="smaller text-uppercase text-muted fw-semibold py-2">My Decks</div>
                <nav className="guides-notebook-switcher">
                  {myDecks.length === 0 && (
                    <div className="text-muted small px-1">No decks yet</div>
                  )}
                  {myDecks.map((nb) => (
                    <button
                      key={nb.id}
                      type="button"
                      className={`guides-notebook-switcher-item${Number(nb.id) === Number(id) ? ' active' : ''}`}
                      onClick={() => {
                        if (Number(nb.id) !== Number(id)) navigate(`/guides/flashcards/decks/${nb.id}`);
                      }}
                    >
                      <div className="guides-notebook-switcher-thumb">
                        <GuideItemIcon item={nb} kind="deck" />
                      </div>
                      <div className="guides-notebook-switcher-meta min-w-0">
                        <span className="guides-notebook-switcher-title">{nb.title}</span>
                        <span className="guides-notebook-switcher-date">
                          {formatGuideDate(nb.createdAt)}
                        </span>
                      </div>
                    </button>
                  ))}
                </nav>
              </div>
            </Col>
          )}

          <Col lg={canEdit ? 9 : 12} md={canEdit ? 9 : 12} className="guides-topic-editor-col">
            <div className="guides-read-scroll guides-manage-scroll pt-3 pe-0">
              <div className="guides-list-header">
                <div className="min-w-0">
                  <h6 className="mb-0 fw-semibold text-truncate">{deck.title}</h6>
                  <p className="guides-page-meta">
                    {setCount} sets · Updated {formatGuideDate(deck.updatedAt)}
                  </p>
                </div>
                {canEdit && (
                  <div className="guides-list-actions">
                    <div className="radio-inputs" style={{ width: 'fit-content' }}>
                      <label className="radio">
                        <input
                          type="checkbox"
                          checked={!!deck.published}
                          onChange={togglePublish}
                          aria-label={deck.published ? 'Published' : 'Publish'}
                        />
                        <span className="name px-md-3 p-2">
                          <i className="bi bi-globe2" aria-hidden />
                          <span className="d-none d-md-inline ms-1">{deck.published ? 'Published' : 'Publish'}</span>
                        </span>
                      </label>
                      <label className="radio">
                        <input
                          type="checkbox"
                          checked={(deck.sharedUserIds || []).length > 0}
                          onChange={() => setShareOpen(true)}
                          aria-label="Shared"
                        />
                        <span className="name px-md-3 p-2">
                          <i className="bi bi-share" aria-hidden />
                          <span className="d-none d-md-inline ms-1">Shared</span>
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
              <div className="guides-sections-head">
                <span className="guides-section-label mb-0">Sections & Flashcard Sets</span>
                {canEdit && (
                  <Button size="sm" variant="light-lite primary-subtle" onClick={() => setSectionModal(true)}>
                    <i className="bi bi-plus-lg me-1" /> Add Section
                  </Button>
                )}
              </div>

              {sections.length === 0 ? (
                <div className="guides-empty card border shadow-none p-4">
                  Add a section to organize flashcard sets.
                </div>
              ) : (
                <Accordion
                  alwaysOpen
                  activeKey={activeSections}
                  onSelect={(keys) => setActiveSections(Array.isArray(keys) ? keys : keys ? [keys] : [])}
                  className="guides-accordion"
                >
                  {sections.map((section) => {
                    const sets = [...(section.sets || [])].sort((a, b) => a.sortOrder - b.sortOrder);
                    return (
                      <Accordion.Item
                        eventKey={String(section.id)}
                        key={section.id}
                        className="guides-section-card"
                      >
                        <div className="guides-section-card-header accordion-header">
                          <Accordion.Button className="guides-section-card-toggle">
                            <span className="fw-semibold">{section.title}</span>
                          </Accordion.Button>
                          <div className="guides-section-card-actions">
                            <span className="text-muted small text-nowrap me-2">
                              {sets.length} {sets.length === 1 ? 'set' : 'sets'}
                            </span>
                            {canEdit && (
                              <>
                                <Button
                                  variant="light-lite primary-subtle"
                                  className="px-2 py-1"
                                  title="Add flashcard set"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSetModal(section.id);
                                    setSetTitle('');
                                  }}
                                >
                                  <i className="bi bi-plus-lg" />
                                </Button>
                                <Button
                                  variant="light-lite danger-lite"
                                  title="Delete section"
                                  className="px-2 py-1"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    deleteSection(section.id);
                                  }}
                                >
                                  <i className="bi bi-trash" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        <Accordion.Body className="guides-section-card-body p-0">
                          {sets.length === 0 ? (
                            <div className="text-muted small px-3 py-3">
                              No flashcard sets yet. Add one to get started.
                            </div>
                          ) : (
                            <div className="guides-topic-rows">
                              {sets.map((fcSet) => (
                                <div
                                  key={fcSet.id}
                                  className="guides-topic-row-item custom-hover-bg-alt"
                                  onDragOver={(e) => {
                                    if (!canEdit || setDragRef.current?.sectionId !== section.id) return;
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = 'move';
                                  }}
                                  onDrop={(e) => {
                                    if (!canEdit) return;
                                    e.preventDefault();
                                    const drag = setDragRef.current;
                                    if (!drag || drag.sectionId !== section.id) return;
                                    if (drag.setId === fcSet.id) return;
                                    const ids = sets.map((t) => t.id);
                                    const from = ids.indexOf(drag.setId);
                                    const to = ids.indexOf(fcSet.id);
                                    if (from < 0 || to < 0) return;
                                    ids.splice(from, 1);
                                    ids.splice(to, 0, drag.setId);
                                    setDragRef.current = null;
                                    document
                                      .querySelectorAll('.guides-topic-row-item.is-dragging')
                                      .forEach((el) => el.classList.remove('is-dragging'));
                                    reorderSectionSets(section.id, ids);
                                  }}
                                >
                                  {canEdit && (
                                    <span
                                      className="guides-topic-drag-handle"
                                      draggable
                                      title="Drag to reorder"
                                      aria-label="Drag to reorder"
                                      onDragStart={(e) => {
                                        setDragRef.current = { sectionId: section.id, setId: fcSet.id };
                                        e.dataTransfer.effectAllowed = 'move';
                                        e.dataTransfer.setData('text/plain', String(fcSet.id));
                                        e.currentTarget.closest('.guides-topic-row-item')?.classList.add('is-dragging');
                                      }}
                                      onDragEnd={(e) => {
                                        setDragRef.current = null;
                                        e.currentTarget.closest('.guides-topic-row-item')?.classList.remove('is-dragging');
                                      }}
                                    >
                                      <i className="bi bi-grip-vertical" aria-hidden />
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    className="guides-topic-row-main"
                                    onClick={() => openSet(fcSet.id)}
                                  >
                                    <span className="fw-semibold d-block">{fcSet.title}</span>
                                    <span className="text-muted small">{formatGuideDate(fcSet.updatedAt)}</span>
                                  </button>
                                  {canEdit ? (
                                    <Dropdown align="end" className="guides-topic-row-menu">
                                      <Dropdown.Toggle
                                        variant="light-lite"
                                        size="sm"
                                        className="guides-topic-menu-toggle px-2 py-1 no-arrow"
                                        id={`fc-set-menu-${fcSet.id}`}
                                      >
                                        <i className="bi bi-three-dots-vertical" aria-hidden />
                                        <span className="visually-hidden">Set options</span>
                                      </Dropdown.Toggle>
                                      <Dropdown.Menu>
                                        <Dropdown.Item
                                          onClick={() => openSet(fcSet.id)}
                                        >
                                          <i className="bi bi-play-circle me-2" aria-hidden />
                                          Open
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          onClick={() => navigate(`/guides/flashcards/sets/${fcSet.id}/edit`)}
                                        >
                                          <i className="bi bi-pencil me-2" aria-hidden />
                                          Edit cards
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          onClick={() => {
                                            setMoveModal(fcSet);
                                            setMoveSectionId(String(section.id));
                                          }}
                                        >
                                          <i className="bi bi-arrow-left-right me-2" aria-hidden />
                                          Move…
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => detachSet(fcSet.id)}>
                                          <i className="bi bi-box-arrow-up-right me-2" aria-hidden />
                                          Make standalone
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          className="text-danger"
                                          onClick={() => deleteSet(fcSet.id)}
                                        >
                                          <i className="bi bi-trash me-2" aria-hidden />
                                          Delete
                                        </Dropdown.Item>
                                      </Dropdown.Menu>
                                    </Dropdown>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="light-lite"
                                      className="px-2 py-1"
                                      onClick={() => openSet(fcSet.id)}
                                    >
                                      <i className="bi bi-play-circle" aria-hidden />
                                      <span className="visually-hidden">Open</span>
                                    </Button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      <Modal show={sectionModal} onHide={() => setSectionModal(false)} centered>
        <Form onSubmit={addSection}>
          <Modal.Header closeButton><Modal.Title>Add Section</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Control
              value={sectionTitle}
              onChange={(e) => setSectionTitle(e.target.value)}
              placeholder="Section title"
              autoFocus
              required
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setSectionModal(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={busy}>Add</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={!!setModal} onHide={() => setSetModal(null)} centered>
        <Form onSubmit={addSet}>
          <Modal.Header closeButton><Modal.Title>Add Flashcard Set</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Control
              value={setTitle}
              onChange={(e) => setSetTitle(e.target.value)}
              placeholder="Set title (topic)"
              autoFocus
              required
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setSetModal(null)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={busy}>Create & edit</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={!!moveModal} onHide={() => setMoveModal(null)} centered>
        <Form onSubmit={moveSet}>
          <Modal.Header closeButton>
            <Modal.Title>Move “{moveModal?.title}”</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Label className="small text-muted">Destination section</Form.Label>
            <Form.Select
              value={moveSectionId}
              onChange={(e) => setMoveSectionId(e.target.value)}
            >
              <option value="">Standalone (no deck)</option>
              {allSectionsFlat.map((s) => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </Form.Select>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setMoveModal(null)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={busy}>Move</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ShareGuideModal
        show={shareOpen}
        onHide={() => setShareOpen(false)}
        targetType="flashcard_deck"
        targetId={Number(id)}
        initialUserIds={deck.sharedUserIds || []}
        onSaved={(shares) => setDeck((d) => ({
          ...d,
          sharedUserIds: (shares || []).map((s) => s.userId)
        }))}
      />
      <PublishGuideModal
        show={publishOpen}
        onHide={() => setPublishOpen(false)}
        targetType="flashcard_deck"
        targetId={Number(id)}
        initialDescription={deck.description || ''}
        initialCategoryId={deck.categoryId}
        initialTopicId={deck.topicId}
        initialAllowEdits={!!deck.allowEdits}
        onPublished={(res) => {
          setDeck((d) => ({
            ...d,
            published: true,
            publishedAt: res.publishedAt,
            allowEdits: res.allowEdits,
            categoryId: res.categoryId,
            topicId: res.topicId,
            description: res.description ?? d.description
          }));
          loadMine();
        }}
      />
    </div>
  );
}
